import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAuth } from '../../hooks/useAuth';
import { useUsers } from '../../hooks/useUsers';
import { fetchUsers } from '../../features/users/usersSlice';
import { ACTIONS } from '../../constants/actionTables';
import NotFoundPage from '../NotFoundPage';
import ModalAddUser from '../../components/ModalAddUser';
import TableComponent from '../../components/Table';
import CustomTabs from '../../components/CustomTabs';
import { Typography } from '@mui/material';
import { requestSearch } from '../../helpers/search';

import styles from './style.module.css';

function UsersList() {
  const [value, setValue] = useState(0);
  const [dataActive, setDataActive] = useState([]);
  const [dataAchived, setDataAchived] = useState([]);
  const [filteredActiveRows, setFilteredActiveRows] = useState([]);
  const [filteredArchivedRows, setFilteredArchivedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const { user } = useAuth();
  const { users } = useUsers();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Load users data on-demand for this page
  useEffect(() => {
    if (!users || (!users.activeUsers?.length && !users.archivedUsers?.length)) {
      dispatch(fetchUsers());
    }
  }, [dispatch, users]);


  const titles = [
    t('generic.number'),
    t('generic.firstName'),
    t('generic.lastName'),
    t('generic.email'),
    t('userList.userGroup'),
    t('generic.action'),
  ];

  useEffect(() => {
    if (users?.activeUsers) {
      const data = users.activeUsers.map((item) => ({
        id: item.id,
        number: item.id,
        firstname: item.firstname,
        lastname: item.lastname,
        email: item.email,
        role: item.role,
      }));
      setDataActive(data);
      setFilteredActiveRows(data);
    }
    if (users?.archivedUsers) {
      const data = users.archivedUsers.map((item) => ({
        id: item.id,
        number: item.id,
        firstname: item.firstname,
        lastname: item.lastname,
        email: item.email,
        role: item.role,
      }));
      setDataAchived(data);
      setFilteredArchivedRows(data);
    }
  }, [users]);

  const handleSearch = (searchValue) => {
    // Reset sorting when searching
    if (searchValue.trim() !== '') {
      setSortConfig({ key: null, direction: 'asc' });
    }

    if (value === 0) {
      requestSearch(searchValue, dataActive, setFilteredActiveRows);
    } else {
      requestSearch(searchValue, dataAchived, setFilteredArchivedRows);
    }
  };

  const getTotalCount = () => {
    return value === 0 ? dataActive.length : dataAchived.length;
  };

  const handleTabChange = (newValue) => {
    setValue(newValue);
    // Reset search and sorting when switching tabs
    setSortConfig({ key: null, direction: 'asc' });
    if (newValue === 0) {
      setFilteredActiveRows(dataActive);
    } else {
      setFilteredArchivedRows(dataAchived);
    }
  };

  const handleSort = (columnKey) => {
    // Implement three-state sorting: asc → desc → reset (no sorting)
    let newSortConfig = { key: null, direction: 'asc' };

    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'asc') {
        // Switch from asc to desc
        newSortConfig = { key: columnKey, direction: 'desc' };
      } else if (sortConfig.direction === 'desc') {
        // Reset sorting (third click)
        newSortConfig = { key: null, direction: 'asc' };
      }
    } else {
      // New column clicked - start with asc
      newSortConfig = { key: columnKey, direction: 'asc' };
    }

    setSortConfig(newSortConfig);

    // If resetting, restore original order
    if (newSortConfig.key === null) {
      if (value === 0) {
        setFilteredActiveRows([...dataActive]);
      } else {
        setFilteredArchivedRows([...dataAchived]);
      }
      return;
    }

    // Apply sorting
    const dataToSort = value === 0 ? [...filteredActiveRows] : [...filteredArchivedRows];

    dataToSort.sort((a, b) => {
      const aValue = a[columnKey];
      const bValue = b[columnKey];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string') {
        return newSortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return newSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    if (value === 0) {
      setFilteredActiveRows(dataToSort);
    } else {
      setFilteredArchivedRows(dataToSort);
    }
  };

  if (user?.role === 'admin') {
    return (
      <section className={styles.container}>
        <div className={styles.header}>
          <div className={styles.caption}>
            <Typography variant="h5" fontWeight="bold" className={styles.headerTitle}>
              {t('userList.userList')}
            </Typography>
            <Typography className={styles.headerSubtitle}>
              {t('generic.manageTeamMembers')}
            </Typography>
          </div>
          <div className={styles.addButton}>
            <ModalAddUser />
          </div>
        </div>

        <div className={styles.tabs}>
          <CustomTabs
            tabs={[
              {
                key: 0,
                label: t('userList.activeUsers'),
                onClick: () => handleTabChange(0),
              },
              {
                key: 1,
                label: t('userList.archivedUsers'),
                onClick: () => handleTabChange(1),
              },
            ]}
            activeTab={value}
          />

          {value === 0 && (
            <TableComponent
              titles={titles}
              data={filteredActiveRows}
              action={ACTIONS.USERS_ACTIVE}
              search={true}
              showTableHeader={true}
              tableTitle={t('generic.allUsers')}
              tableCount={`${getTotalCount()} ${t('generic.activeUsers')} ${t(
                'generic.usersText'
              )}`}
              searchPlaceholder={t('generic.search')}
              onSearch={handleSearch}
              onSort={handleSort}
              sortConfig={sortConfig}
              sortableColumns={['number', 'firstname', 'lastname', 'email', 'role']}
              columnKeys={['number', 'firstname', 'lastname', 'email', 'role', 'action']}
            />
          )}
          {value === 1 && (
            <TableComponent
              titles={titles}
              data={filteredArchivedRows}
              action={ACTIONS.USERS_ARCHIVED}
              search={true}
              showTableHeader={true}
              tableTitle={t('generic.allUsers')}
              tableCount={`${getTotalCount()} ${t('generic.archivedUsers')} ${t(
                'generic.usersText'
              )}`}
              searchPlaceholder={t('generic.search')}
              onSearch={handleSearch}
              onSort={handleSort}
              sortConfig={sortConfig}
              sortableColumns={['number', 'firstname', 'lastname', 'email', 'role']}
              columnKeys={['number', 'firstname', 'lastname', 'email', 'role', 'action']}
            />
          )}
        </div>
      </section>
    );
  } else {
    return <NotFoundPage />;
  }
}

export default UsersList;
